import React from 'react';
import './invoice-card.scss';
import { TouchableOpacity } from '../../common';
import { SVGS } from '../../assets';
import { StatusBadge } from '../../components';
import { MessageStatus } from '../../constants';
import {
  dispatch,
  getSelectedItems,
  Invoice,
  updateSelectedItemsMap,
  useAppSelector,
} from '@zonofi/zono-money-store';
import moment from 'moment';
import { getFormattedAmount } from '@zonofi/common/functions/format-amount';
import { InvoiceSeenStatus } from '../invoice-seen-status';
import { getInvoiceStatus, getInvoiceStatusStyle } from '../../helper';
import { ReactComponent as Checkbox } from '@zonofi/svg-icons/checkbox.svg';
import { ReactComponent as Checked } from '@zonofi/svg-icons/checked.svg';
import { ReactComponent as ShareIcon } from '@zonofi/svg-icons/forward-icon.svg';
import { useLocation } from 'react-router-dom';

interface InvoiceCardProps {
  invoiceItem: Invoice;
  showSelection?: boolean;
  showDeletion?: boolean;
  isDisable?: boolean;
  onInvoiceItemPress?: (invoiceItem: Invoice) => void;
  onDeletePress?: (invoiceItem: Invoice) => void;
  onShareInvoice?: (invoiceItem: Invoice) => void;
  showShare?: boolean;
}

export const InvoiceCard: React.FC<InvoiceCardProps> = ({
  invoiceItem,
  showSelection = false,
  showDeletion = false,
  isDisable = false,
  onInvoiceItemPress = () => {},
  onDeletePress = () => {},
  onShareInvoice = () => {},
  showShare = true,
}) => {
  const location = useLocation();
  const customerIdFromParam = location.pathname.split('/').pop();

  const selectedInvoiceItems = useAppSelector(getSelectedItems);
  const isSelected = selectedInvoiceItems.some(
    (selected) => selected.id === invoiceItem.id
  );

  const statusPriority: {
    field: string;
    status: MessageStatus;
  }[] = [
    { field: 'engaged', status: MessageStatus.ENGAGED },
    { field: 'MESSAGE_SEEN', status: MessageStatus.MESSAGE_SEEN },
    { field: 'MESSAGE_DELIVERED', status: MessageStatus.MESSAGE_DELIVERED },
    { field: 'MESSAGE_SENT', status: MessageStatus.MESSAGE_SENT },
  ];

  const messageStatus: MessageStatus | null = invoiceItem?.messageStatus
    ? (statusPriority.find(({ field }) => field in invoiceItem.messageStatus)
        ?.status ?? null)
    : null;

  return (
    <TouchableOpacity
      className="invoice-card"
      onPress={() => {
        onInvoiceItemPress(invoiceItem);
      }}
      disabled={isDisable}
      allowAnimation={false}
    >
      <div className="invoice-card-content">
        {/* Header */}
        <div className="invoice-card-content-header">
          <div className="invoice-card-content-header-top">
            <div className="invoice-card-content-header-top-left">
              {showSelection &&
                (isSelected ? (
                  <Checked
                    className="invoice-card-content-header-top-left-checkbox"
                    color={'#000000'}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      dispatch(updateSelectedItemsMap(invoiceItem));
                    }}
                  />
                ) : (
                  <Checkbox
                    className="invoice-card-content-header-top-left-checkbox"
                    color={'#8C8C8C'}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      dispatch(updateSelectedItemsMap(invoiceItem));
                    }}
                  />
                ))}
              <div className="invoice-card-content-header-top-left-info">
                <span className="invoice-card-content-header-top-left-info-issue font-semibold">
                  {invoiceItem?.docNumber}
                </span>
                <span className="invoice-card-content-header-top-left-info-details font-regular">
                  {moment(invoiceItem?.invoiceDate).format('DD MMM, YY')} •{' '}
                  {invoiceItem?.customerName || 'N/A'}
                </span>
              </div>
            </div>
            <div className="invoice-card-content-header-top-right">
              <div className="invoice-card-content-header-top-right-left">
                <StatusBadge
                  status={getInvoiceStatus(invoiceItem?.invoiceStatus ?? '')}
                  style={getInvoiceStatusStyle(
                    invoiceItem?.invoiceStatus ?? ''
                  )}
                />
                <div className="invoice-card-content-header-top-right-left-status">
                  <InvoiceSeenStatus
                    messageStatus={messageStatus}
                    key={invoiceItem?.id}
                  />
                </div>
              </div>
              <div className="invoice-card-content-header-top-right-right">
                {showDeletion && (
                  <TouchableOpacity
                    className="invoice-card-content-header-top-right-img"
                    onPress={() => onDeletePress?.(invoiceItem)}
                  >
                    <img
                      src={SVGS.DeleteIcon}
                      alt="remove invoice from selection"
                    />
                  </TouchableOpacity>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Extra Details */}
        <div className="invoice-card-content__extra-details">
          <div className="invoice-card-content__extra-details-item">
            <div className="invoice-card-content__extra-details-item-heading font-regular">
              Amount
            </div>
            <div className="invoice-card-content__extra-details-item-value font-semibold">
              {getFormattedAmount(invoiceItem?.invoiceAmount, true)}
            </div>
          </div>
          <div className="invoice-card-content__extra-details-item">
            <div className="invoice-card-content__extra-details-item-heading font-regular">
              Due Date
            </div>
            <div className="invoice-card-content__extra-details-item-value font-semibold">
              {moment(invoiceItem?.dueDate).format('DD MMM, YY')}
            </div>
          </div>
          <div className="invoice-card-content__extra-details-item">
            <div className="invoice-card-content__extra-details-item-heading font-regular">
              Outstanding
            </div>
            <div className="invoice-card-content__extra-details-item-value font-semibold">
              {!invoiceItem?.outStandingAmount
                ? getFormattedAmount(
                    (invoiceItem?.invoiceAmount ?? 0) -
                      (invoiceItem?.paidAmount ?? 0),
                    true
                  )
                : getFormattedAmount(invoiceItem?.outStandingAmount, true)}
            </div>
          </div>
          {showShare && invoiceItem?.docs && invoiceItem?.docs?.length > 0 && (
            <div className="invoice-card-content__extra-details-item">
              <ShareIcon
                color="#000000"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onShareInvoice(invoiceItem);
                }}
                width={24}
                height={24}
              />
            </div>
          )}
        </div>
      </div>
    </TouchableOpacity>
  );
};
